/**
 * Saved levels in this browser: where `level.save` (autosave, Ctrl+S) puts a
 * level's edits, and where a level opens them from.
 *
 * A save is the whole level in the manifest format (`serializeWorld`), which
 * is already a versioned contract, rather than the command journal, which
 * isn't one yet (CLAUDE.md rule 11). It's kept per level with the manifest's
 * content hash, so edits against an older version of a level are dropped
 * instead of misapplied. Whether saves survive a reload is the visitor's
 * choice (the `keepSavedEdits` preference, applied at boot by the module).
 */
import { MANIFEST_FORMAT_VERSION } from "../../assets/content";
import { upgradeEntities } from "../../assets/manifestUpgrades";
import type { ManifestEntity } from "../../assets/content";
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";

/** Where saves live: localStorage in the app, a map in tests. */
export interface SaveStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  keys(): string[];
}

/** Every save's storage key starts with this; the level id follows. */
export const SAVE_KEY_PREFIX = "kn.save.";

interface SavedLevel {
  /** The level format its entities are written in (MANIFEST_FORMAT_VERSION when saved). */
  readonly formatVersion: number;
  /** The content hash of the manifest the edits were made against. */
  readonly hash: string;
  readonly entities: readonly ManifestEntity[];
}

/** A save as stored: the entities, and the manifest hash they were made against. */
export interface LevelSave {
  readonly hash: string;
  readonly entities: readonly ManifestEntity[];
}

export class LevelSaves {
  /** The ids of levels with a save: the Content Browser's "modified" marks. */
  readonly savedLevels: ReadableStore<ReadonlySet<string>>;
  private readonly saved: VersionedStore<ReadonlySet<string>>;

  /** `storage` is null when the browser offers none (privacy modes). */
  constructor(
    private readonly storage: SaveStorage | null,
    scheduler: ChangeScheduler,
  ) {
    this.saved = new VersionedStore<ReadonlySet<string>>(scheduler, new Set(this.storedIds()));
    this.savedLevels = this.saved;
  }

  save(levelId: string, hash: string, entities: readonly ManifestEntity[]): void {
    if (!this.storage) throw new Error("This browser isn't keeping saves (storage is off)");
    const saved: SavedLevel = { formatVersion: MANIFEST_FORMAT_VERSION, hash, entities };
    this.storage.setItem(SAVE_KEY_PREFIX + levelId, JSON.stringify(saved));
    if (!this.saved.getSnapshot().has(levelId)) this.publish();
  }

  /** The stored save for a level, whatever version of the level it was made against. */
  peek(levelId: string): LevelSave | null {
    const text = this.storage?.getItem(SAVE_KEY_PREFIX + levelId) ?? null;
    const saved = text === null ? null : parseSave(text);
    return saved && { hash: saved.hash, entities: saved.entities };
  }

  remove(levelId: string): void {
    this.storage?.removeItem(SAVE_KEY_PREFIX + levelId);
    this.publish();
  }

  /** The level's saved entities, if a save exists for this version of it. A stale save is removed. */
  load(levelId: string, hash: string): readonly ManifestEntity[] | null {
    const text = this.storage?.getItem(SAVE_KEY_PREFIX + levelId) ?? null;
    if (text === null) return null;

    const saved = parseSave(text);
    if (saved?.hash === hash) return saved.entities;
    this.remove(levelId);
    return null;
  }

  clearAll(): void {
    for (const levelId of this.storedIds()) this.storage?.removeItem(SAVE_KEY_PREFIX + levelId);
    this.publish();
  }

  private storedIds(): string[] {
    return (this.storage?.keys() ?? [])
      .filter((key) => key.startsWith(SAVE_KEY_PREFIX))
      .map((key) => key.slice(SAVE_KEY_PREFIX.length));
  }

  private publish(): void {
    this.saved.publish(new Set(this.storedIds()));
  }
}

/** A stored save, its entities upgraded to the current format; null if unreadable or too new. */
function parseSave(text: string): SavedLevel | null {
  try {
    const value = JSON.parse(text) as Partial<SavedLevel> | null;
    const version = value?.formatVersion;
    if (typeof version !== "number" || version < 1 || version > MANIFEST_FORMAT_VERSION)
      return null;
    if (typeof value?.hash !== "string" || !Array.isArray(value.entities)) return null;
    return {
      formatVersion: MANIFEST_FORMAT_VERSION,
      hash: value.hash,
      entities: upgradeEntities(value.entities, version),
    };
  } catch {
    return null;
  }
}
